export interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  createdAt: string
  link?: string
}

export interface NotificationResponse {
  success: boolean
  notifications?: Notification[]
  unreadCount?: number
  error?: string
}

class NotificationService {
  private baseUrl = (import.meta.env.VITE_API_URL as string) || ''

  async getNotifications(): Promise<NotificationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/notifications`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: data.message || 'Failed to fetch notifications'
        }
      }

      return {
        success: true,
        notifications: data.notifications || [],
        unreadCount: data.unreadCount || 0
      }
    } catch (error) {
      // Error handling - notifications are optional
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching notifications:', error)
      }
      return {
        success: false,
        error: 'Failed to fetch notifications'
      }
    }
  }

  async markAsRead(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: data.message || 'Failed to mark notification as read'
        }
      }

      return {
        success: true
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking notification as read:', error)
      }
      return {
        success: false,
        error: 'Failed to mark notification as read'
      }
    }
  }

  async markAllAsRead(): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: data.message || 'Failed to mark all notifications as read'
        }
      }

      return {
        success: true
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking all notifications as read:', error)
      }
      return {
        success: false,
        error: 'Failed to mark all notifications as read'
      }
    }
  }

  async clearAll(): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/notifications/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: data.message || 'Failed to clear notifications'
        }
      }

      return {
        success: true
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error clearing notifications:', error)
      }
      return {
        success: false,
        error: 'Failed to clear notifications'
      }
    }
  }
}

export const notificationService = new NotificationService()
